import _toConsumableArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _taggedTemplateLiteral from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/taggedTemplateLiteral.js";

var _templateObject, _templateObject2;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { S$ } from '@infl-ts/util-fp-ts';
import { useToggle } from '@infl-ts/util-hooks';
import { makeDecodeError } from '@infl-ts/util-make-apollo-decoded';
import { logError, makeStreamError } from '@infl-ts/util-query-errors';
import { join } from '@infl-ts/util-string-join';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { styled } from '@mui/material';
import * as A from 'fp-ts/Array';
import * as E from 'fp-ts/Either';
import * as Eq from 'fp-ts/Eq';
import { constant, constVoid, flow, identity, pipe } from 'fp-ts/function';
import * as NEA from 'fp-ts/NonEmptyArray';
import * as O from 'fp-ts/Option';
import * as Ord from 'fp-ts/Ord';
import * as Pred from 'fp-ts/Predicate';
import * as Str from 'fp-ts/string';
import * as S from 'fp-ts/struct';
import * as T from 'fp-ts/Task';
import * as TE from 'fp-ts/TaskEither';
import { nanoid } from 'nanoid';
import { useEffect } from 'react';
import { FetchUsersResponse } from './codecs';
export var defaultMemberName = 'Unnamed User'; // stream-chat@8.3.0 does not list `notification.mark_unread` in `EventTypes`, but the
// server emits it and the client dispatches events by name at runtime.

export var markUnreadEvent = 'notification.mark_unread'; // Dispatched locally once a mark-as-unread has been applied *and* the channel's read state
// has been re-queried. Deliberately not `markUnreadEvent`: the server's own websocket event
// for the same action arrives while local read state is still stale, so anything keyed on it
// would recompute counts from the pre-mark values.

export var markUnreadSettledEvent = 'local.mark_unread_settled'; // The live active channel. `MarkAsUnread` needs it: the action runs from the preview's
// ancillary menu, which unmounts the instant it is clicked, so the component's own
// `activeChannel` is frozen at click time while its work runs on for two more round trips.
// Kept current by `SwitchChatView`, which outlives every `chatView`.

export var activeChannelRef = {}; // Neither field is in the 8.3.0 `Event` type, for the same reason the event name is not in
// `EventTypes`: the SDK predates mark-as-unread. The server sends both.

// stream-chat@8.3.0 has no handler for `notification.mark_unread`, so an incoming one leaves
// local read state on its pre-mark values: `channel.lastRead()` does not move and
// `channel.state.unreadCount` -- what `channel.countUnread()` returns -- stays stale. Apply
// it the way the SDK applies `message.read`, so a mark made in another tab or on another
// device reaches the badges without a re-query. The event's own fields are authoritative and
// agree with what re-querying the channel returns. Bail rather than write a bogus read state
// if they ever stop arriving.
//
// Returns whether the state was applied. The caller has to know: dropping the "mark all as
// read" mask for a channel whose local read state was *not* repaired makes its badge
// recompute the entire backlog the mask exists to hide.
//
// For whoever next bumps stream-chat: this reaches into channel.state.read and
// channel.state.unreadCount directly, behind casts that hide the types the SDK does not
// have yet. Check whether the new version handles notification.mark_unread itself, and
// delete this in favour of it if it does.
export var applyRemoteMarkUnread = function applyRemoteMarkUnread(channel, client, event) {
  var _event$user;

  var _ref = event,
      lastReadAt = _ref.last_read_at,
      unread = _ref.unread_messages;
  if (!((_event$user = event.user) !== null && _event$user !== void 0 && _event$user.id) || !lastReadAt || unread == null) return false;
  channel.state.read[event.user.id] = {
    last_read: new Date(lastReadAt),
    user: event.user,
    unread_messages: unread
  };
  if (event.user.id === client.userID) channel.state.unreadCount = unread;
  return true;
}; // Whether a message counts towards the current user's unread badge. Covers the per-message
// exclusions of the SDK's own `_countMessageAsUnread` but not its two channel-level ones
// (a missing `read-events` capability, and a muted channel), so this count deliberately
// still shows a badge on a muted channel where `channel.countUnread()` reports zero.
// Do not add exclusions here for one caller's benefit: `channel.countUnread()`, the nav
// badge and the sidebar's "Unread" filter all have to keep agreeing on the per-message
// rules. Anything narrower belongs in a predicate of its own, as `canAnchorMarkUnread` is.

export var countMessageAsUnread = function countMessageAsUnread(client) {
  return function (message) {
    var _message$user, _message$user2;

    if (message.shadowed) return false;
    if (message.silent) return false;
    if (message.parent_id && !message.show_in_channel) return false;
    if (((_message$user = message.user) === null || _message$user === void 0 ? void 0 : _message$user.id) === client.userID) return false;
    if ((_message$user2 = message.user) !== null && _message$user2 !== void 0 && _message$user2.id && client.userMuteStatus(message.user.id)) return false;
    if (message.type === 'system') return false;
    return true;
  };
}; // Whether `markUnread` can use this message as its anchor. A message that does not count
// towards the badge would mark the channel unread with a count of zero, and the API rejects
// a deleted message outright. Kept separate from `countMessageAsUnread` so tightening the
// anchor rules cannot quietly change what the unread badges count.

export var canAnchorMarkUnread = function canAnchorMarkUnread(client) {
  return function (message) {
    return message.type !== 'deleted' && !message.deleted_at && countMessageAsUnread(client)(message);
  };
};
export var VerifiedAdminIcon = styled(VerifiedUserIcon)(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  font-size: 18px;\n  top: 3px;\n  position: relative;\n  color: ", ";\n"])), function (p) {
  return p.theme.palette.text.secondary;
});
export var LockedUserIcon = styled(LockIcon)(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  font-size: 16px;\n  top: 3px;\n  position: relative;\n  color: ", ";\n"])), function (p) {
  return p.theme.palette.text.secondary;
});
export var useChannelMuteStatus = function useChannelMuteStatus(client, channel) {
  var _useToggle = useToggle(channel.muteStatus().muted),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      isMuted = _useToggle2[0],
      _useToggle2$ = _useToggle2[1],
      mute = _useToggle2$.on,
      unMute = _useToggle2$.off;

  useEffect(function () {
    var handler = function handler() {
      var recentlyMuted = channel.muteStatus().muted;
      if (recentlyMuted && !isMuted) mute();
      if (!recentlyMuted && isMuted) unMute();
    };

    client.on('notification.channel_mutes_updated', handler);
    return function () {
      client.off('notification.channel_mutes_updated', handler);
    };
  }, [channel, client, isMuted, mute, unMute]);
  return isMuted;
};
export var queryChannels = function queryChannels(_ref2) {
  var client = _ref2.client,
      hubId = _ref2.hubId,
      filter = _ref2.filter,
      sort = _ref2.sort,
      options = _ref2.options,
      stateOptions = _ref2.stateOptions;
  return TE.tryCatch(function () {
    return client.queryChannels(_objectSpread({
      type: 'messaging',
      team: {
        $eq: hubId
      }
    }, filter), sort, options, stateOptions);
  }, makeStreamError);
};
export var getMostRecentChannel = function getMostRecentChannel(client, hubId) {
  return pipe(client.user, TE.fromNullable(makeStreamError('No client user')), TE.chain(function (clientUser) {
    return queryChannels({
      client: client,
      hubId: hubId,
      filter: {
        members: {
          $in: [clientUser.id]
        },
        last_message_at: {
          $lt: new Date().toISOString()
        }
      },
      sort: {
        last_updated: -1
      },
      options: {
        limit: 1,
        presence: true
      }
    });
  }));
};
export var isAdmin = function isAdmin(user) {
  return (user === null || user === void 0 ? void 0 : user.infl_role) === 'admin' || (user === null || user === void 0 ? void 0 : user.infl_role) === 'super_admin';
};
export var isOwner = function isOwner(member) {
  return member.role === 'owner';
};
export var isLocked = function isLocked(member) {
  var _member$user;

  return ((_member$user = member.user) === null || _member$user === void 0 ? void 0 : _member$user.locked_at) != null;
};
export var isDeactivated = function isDeactivated(member) {
  var _member$user2;

  return !!((_member$user2 = member.user) !== null && _member$user2 !== void 0 && _member$user2.deactivated_at);
};
export var getAllChannelMembers = function getAllChannelMembers(channel) {
  var includeDeleted = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return pipe(O.fromNullable(channel === null || channel === void 0 ? void 0 : channel.state.members), O.map(function (members) {
    return Object.values(members);
  }), includeDeleted ? identity : O.map(A.filter(Pred.not(isDeactivated))), O.filter(A.isNonEmpty));
};
export var getOtherChannelMembers = function getOtherChannelMembers(channel, clientId) {
  var includeDeleted = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  if (!clientId) return O.none;
  return pipe(O.fromNullable(channel === null || channel === void 0 ? void 0 : channel.state.members), O.map(function (members) {
    return Object.values(members);
  }), O.map(A.filter(function (member) {
    var _member$user3;

    return ((_member$user3 = member.user) === null || _member$user3 === void 0 ? void 0 : _member$user3.id) !== clientId && (includeDeleted || !isDeactivated(member));
  })), O.filter(A.isNonEmpty));
};
export var getOtherChannelResponseMembers = function getOtherChannelResponseMembers(channel, clientId) {
  var includeDeleted = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  if (!clientId) return O.none;
  return pipe(O.fromNullable(channel === null || channel === void 0 ? void 0 : channel.members), O.map(A.filter(function (member) {
    var _member$user4;

    return ((_member$user4 = member.user) === null || _member$user4 === void 0 ? void 0 : _member$user4.id) !== clientId && (includeDeleted || !isDeactivated(member));
  })), O.filter(A.isNonEmpty));
};
export var rebuildQueryableName = function rebuildQueryableName(members, isAdminType) {
  return pipe(members, A.filterMap(function (_ref3) {
    var user = _ref3.user;
    return !isAdminType || !isAdmin(user) ? O.fromNullable(user === null || user === void 0 ? void 0 : user.name) : O.none;
  }), join(', '), function (s) {
    return isAdminType ? Str.isEmpty(s) ? 'Admins' : 'Admins & '.concat(s) : s;
  });
};

var getExistingChannelByUsers = function getExistingChannelByUsers(_ref4) {
  var client = _ref4.client,
      hubId = _ref4.hubId,
      _ref4$isAdmin = _ref4.isAdmin,
      isAdmin = _ref4$isAdmin === void 0 ? false : _ref4$isAdmin;
  return function (users) {
    return pipe(client.user, TE.fromNullable(makeStreamError('No client user')), TE.chain(function (clientUser) {
      return pipe(users, A.map(function (user) {
        return user.id;
      }), A.append(clientUser.id), function (memberIds) {
        return queryChannels({
          client: client,
          hubId: hubId,
          filter: _objectSpread({
            members: {
              $eq: memberIds
            },
            $or: [{
              hidden: {
                $eq: true
              }
            }, {
              hidden: {
                $eq: false
              }
            }]
          }, isAdmin && clientUser.primary_admin && {
            created_by_id: {
              $in: memberIds
            }
          }),
          options: {
            limit: 1,
            presence: true
          }
        });
      });
    }));
  };
};

var addMemberToChannel = function addMemberToChannel(member) {
  return function (channel) {
    return pipe(TE.tryCatch(function () {
      return channel.addMembers([member.id], {
        text: '{{users}} joined the group',
        modifiedUsers: member.name || member.id
      });
    }, makeStreamError), TE.chain(function (response) {
      return TE.tryCatch(function () {
        var _channel$data;

        return channel.updatePartial({
          set: {
            name: rebuildQueryableName(response.members, ((_channel$data = channel.data) === null || _channel$data === void 0 ? void 0 : _channel$data.channel_type) === 'admin')
          }
        });
      }, makeStreamError);
    }));
  };
};

var getLastCreatedAdminChannel = function getLastCreatedAdminChannel(client, admins, hubId) {
  return pipe(client.user, TE.fromNullable(makeStreamError('No client user')), TE.bindTo('clientUser'), TE.bind('adminIds', function () {
    return TE.of(admins.map(function (_ref5) {
      var id = _ref5.id;
      return id;
    }));
  }), TE.bind('results', function (_ref6) {
    var clientUser = _ref6.clientUser,
        adminIds = _ref6.adminIds;
    return queryChannels({
      client: client,
      hubId: hubId,
      filter: {
        channel_type: {
          $eq: 'admin'
        },
        members: {
          $eq: adminIds
        },
        created_by_id: clientUser.primary_admin ? {
          $in: adminIds
        } : {
          $eq: clientUser.id
        }
      },
      sort: {
        created_at: -1
      },
      options: {
        limit: 1,
        presence: true
      }
    });
  }), TE.chain(function (_ref7) {
    var clientUser = _ref7.clientUser,
        results = _ref7.results;
    return pipe(results, A.match(function () {
      return TE.right(results);
    }, flow(NEA.head, addMemberToChannel(clientUser), TE.chain(function () {
      return TE.right(results);
    }))));
  }));
};

var getExistingAdminChannel = function getExistingAdminChannel(_ref8) {
  var client = _ref8.client,
      hubId = _ref8.hubId;
  return function (admins) {
    return pipe(admins, getExistingChannelByUsers({
      client: client,
      hubId: hubId,
      isAdmin: true
    }), TE.chain(A.match(function () {
      return getLastCreatedAdminChannel(client, admins, hubId);
    }, TE.right)));
  };
};

export var getExistingChannelById = function getExistingChannelById(_ref9) {
  var client = _ref9.client,
      hubId = _ref9.hubId,
      channelId = _ref9.channelId,
      onSuccess = _ref9.onSuccess;
  return pipe(queryChannels({
    client: client,
    hubId: hubId,
    filter: {
      cid: {
        $eq: channelId
      },
      $or: [{
        hidden: {
          $eq: true
        }
      }, {
        hidden: {
          $eq: false
        }
      }]
    },
    options: {
      limit: 1,
      presence: true
    }
  }), TE.foldW(function (err) {
    return T.of(pipe(err, logError));
  }, function (res) {
    return T.of(pipe(res, A.match(constVoid, function (channel) {
      return onSuccess(NEA.head(channel));
    })));
  }), function (invokeTask) {
    return invokeTask();
  });
};

var createNewChannel = function createNewChannel(_ref10) {
  var users = _ref10.users,
      client = _ref10.client,
      hubId = _ref10.hubId,
      channelType = _ref10.channelType,
      name = _ref10.name;
  return pipe(client.user, TE.fromNullable(makeStreamError('No client user')), TE.chain(function () {
    return TE.tryCatch(function () {
      return pipe(users, A.map(S$.prop('id')), client.userID ? A.append(client.userID) : identity, function (members) {
        return client.channel('messaging', nanoid(), {
          name: name,
          members: members,
          team: hubId,
          channel_type: channelType
        });
      }, function (newChannel) {
        return newChannel.watch().then(function () {
          return newChannel.show();
        }).then(function () {
          return newChannel;
        });
      });
    }, makeStreamError);
  }));
};

export var getOrCreateChannel = function getOrCreateChannel(client, hubId) {
  return function (_ref11) {
    var users = _ref11.users,
        name = _ref11.name,
        isAdmins = _ref11.isAdmins,
        onSuccess = _ref11.onSuccess;
    return pipe(isAdmins ? getExistingAdminChannel : getExistingChannelByUsers, function (getChannelTE) {
      return getChannelTE({
        client: client,
        hubId: hubId
      })(users);
    }, TE.chain(A.match(function () {
      return createNewChannel({
        users: users,
        client: client,
        hubId: hubId,
        channelType: isAdmins ? 'admin' : 'user',
        name: name
      });
    }, flow(NEA.head, TE.right))), T.map(E.foldW(function (err) {
      return pipe(err, logError);
    }, onSuccess)), function (invokeTask) {
      return invokeTask();
    });
  };
};
export var openToChannel = function openToChannel(setActiveChannel, setChatView) {
  return function (channel) {
    void pipe(TE.tryCatch(constant(channel.watch({
      presence: true
    })), identity), TE.matchE(function (e) {
      return T.of(pipe(e, logError, constVoid));
    }, function () {
      return T.fromIO(function () {
        setActiveChannel && setActiveChannel(channel);
        setChatView('inner');
      });
    }))();
  };
};
export var createUserChannelName = function createUserChannelName(clientUserName, users) {
  return pipe(users, A.map(function (member) {
    var _member$name;

    return (_member$name = member.name) !== null && _member$name !== void 0 ? _member$name : defaultMemberName;
  }), A.append(clientUserName !== null && clientUserName !== void 0 ? clientUserName : defaultMemberName), join(', '));
};
export var createAdminChannelName = function createAdminChannelName(clientUserName) {
  return clientUserName ? 'Admins & ' + clientUserName : 'Admins';
};
export var isDeprecatedChannel = function isDeprecatedChannel(channelID) {
  return channelID === null || channelID === void 0 ? void 0 : channelID.includes('!members-');
}; // TODO: Remove in ADVO2-2287

export var toChatIds = A.map(function (_ref12) {
  var id = _ref12.id;
  return id.replace('member_', '');
});
export var byMemberName = pipe(Str.Ord, Ord.contramap(function (member) {
  var _member$user5;

  return (_member$user5 = member.user) !== null && _member$user5 !== void 0 && _member$user5.name ? Str.toLowerCase(member.user.name) : 'z';
}));
export var getOtherUser = function getOtherUser(channel, client) {
  return pipe(getOtherChannelMembers(channel, client.userID, false), O.filter(function (members) {
    return members.length === 1;
  }), O.map(NEA.head));
};
export var queryChatAdmins = function queryChatAdmins(client) {
  return pipe(TE.tryCatch(function () {
    return client.queryUsers({
      infl_role: {
        $in: ['admin', 'author']
      },
      primary_admin: {
        $eq: true
      }
    }, {
      name: 1
    }, {
      limit: 50
    });
  }, identity), TE.chainEitherKW(function (response) {
    return pipe(FetchUsersResponse.decode(response), E.mapLeft(makeDecodeError));
  }), TE.map(function (_ref13) {
    var users = _ref13.users;
    return users;
  }), TE.map(A.filter(function (user) {
    return user.id !== client.userID;
  })), TE.mapLeft(console.error));
};
export var byUserName = pipe(Str.Ord, Ord.contramap(function (user) {
  return Str.toLowerCase(user.name);
}));
export var queryChatUsers = function queryChatUsers(_ref14) {
  var searchUserQuery = _ref14.searchUserQuery,
      advocateMessagingEnabled = _ref14.advocateMessagingEnabled,
      client = _ref14.client,
      filteredIDs = _ref14.filteredIDs,
      hubId = _ref14.hubId;
  if (searchUserQuery.trim() === '') return TE.left(makeStreamError('Empty search query'));
  return pipe(TE.tryCatch(function () {
    var _client$user;

    return client.queryUsers(_objectSpread({
      name: {
        $autocomplete: searchUserQuery
      },
      locked_at: {
        $eq: null
      },
      teams: {
        $contains: hubId
      }
    }, !advocateMessagingEnabled && ((_client$user = client.user) === null || _client$user === void 0 ? void 0 : _client$user.infl_role) !== 'admin' ? {
      infl_role: 'admin'
    } : {
      $or: [{
        infl_role: 'admin'
      }, {
        infl_role: null
      }]
    }), {
      name: 1
    }, {
      limit: 50
    });
  }, makeStreamError), TE.chainEitherKW(function (response) {
    return pipe(FetchUsersResponse.decode(response), E.mapLeft(makeDecodeError));
  }), TE.map(S$.partialEvolve({
    users: flow(A.filter(function (_ref15) {
      var id = _ref15.id;
      return !A.elem(Str.Eq)(id, filteredIDs);
    }), A.partition(function (_ref16) {
      var name = _ref16.name;
      return Str.startsWith(searchUserQuery.toLowerCase())(name.toLowerCase());
    }), S.evolve({
      left: A.sort(byUserName),
      right: A.sort(byUserName)
    }), function (_ref17) {
      var left = _ref17.left,
          right = _ref17.right;
      return [].concat(_toConsumableArray(right), _toConsumableArray(left));
    })
  })));
};
export var queryUsersById = function queryUsersById(userIds) {
  return function (client) {
    return pipe(TE.tryCatch(function () {
      return client.queryUsers({
        id: {
          $in: userIds
        }
      });
    }, identity), TE.chainEitherKW(function (response) {
      return pipe(FetchUsersResponse.decode(response), E.mapLeft(makeDecodeError));
    }), TE.map(function (_ref18) {
      var users = _ref18.users;
      return users;
    }), TE.mapLeft(flow(logError, constVoid)));
  };
};
export var searchChannels = function searchChannels(searchChannelQuery, client, hubId) {
  if (searchChannelQuery === '') return TE.left(makeStreamError('Empty search query'));
  if (!(client !== null && client !== void 0 && client.user)) return TE.left(makeStreamError('No client user'));
  return queryChannels({
    client: client,
    hubId: hubId,
    filter: {
      $or: [{
        name: {
          $autocomplete: searchChannelQuery
        }
      }, {
        custom_channel_name: {
          $autocomplete: searchChannelQuery
        }
      }],
      members: {
        $in: [client.user.id]
      },
      last_message_at: {
        $lt: new Date().toISOString()
      }
    },
    options: {
      limit: 30,
      presence: true
    }
  });
};
export var searchMessages = function searchMessages(_ref19) {
  var searchMessageQuery = _ref19.searchMessageQuery,
      client = _ref19.client;
  if (searchMessageQuery.trim() === '') return TE.left(makeStreamError('Empty search query'));
  return pipe(client.user, TE.fromNullable(makeStreamError('No client user')), TE.chain(function (clientUser) {
    return TE.tryCatch(function () {
      return client.search({
        members: {
          $in: [clientUser.id]
        }
      }, {
        text: {
          $autocomplete: searchMessageQuery
        }
      }, {
        sort: [{
          relevance: -1
        }, {
          created_at: -1
        }, {
          updated_at: -1
        }],
        limit: 20
      });
    }, makeStreamError);
  }), TE.map(function (_ref20) {
    var results = _ref20.results;
    return pipe(results, A.map(function (_ref21) {
      var message = _ref21.message;
      return message;
    }), A.uniq(Eq.fromEquals(function (x, y) {
      return x.cid === y.cid;
    })));
  }));
};